/**
 * Header Mode Manager
 *
 * Handles the transparent/opaque mode of the header based on sentinel elements visibility.
 * Uses IntersectionObserver to detect when hero sections or dark zones are visible.
 *
 * Usage:
 * 1. Add `data-header-sentinel` attribute to elements that should trigger transparent mode
 * 2. Import and call `initHeaderMode()` on page load
 */

let observer: IntersectionObserver | null = null;
let intersectingSentinels = new Set<Element>();
let collapseScrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;
let filterBarStickyObserver: ResizeObserver | null = null;
let initialLoad = true;

const MARKETPLACE_FILTER_BAR_STICKY_TOP_VAR =
    "--marketplace-filter-bar-sticky-top";

// Sales banner has a CSS-fixed height: 60px content (.sales-banner__inner) + 1px
// border-bottom (.sales-banner). It never varies with content or viewport, so we
// use a constant instead of reading offsetHeight (which forces a layout reflow).
// Must stay in sync with the :root default in resources/css/sales-banner.scss.
const SALES_BANNER_HEIGHT_PX = 61;

function isMarketplaceCompactLayout(): boolean {
    return document.documentElement.classList.contains(
        "marketplace-compact-layout",
    );
}

/**
 * Check if we're on desktop viewport (>= 960px).
 */
function isDesktop(): boolean {
    return typeof window !== "undefined" && window.innerWidth >= 960;
}

/**
 * Check if a sentinel should be active based on viewport.
 * Sentinels with data-header-sentinel="desktop-only" only work on desktop.
 */
function isSentinelActive(el: Element): boolean {
    const sentinelValue = el.getAttribute("data-header-sentinel");
    if (sentinelValue === "desktop-only") {
        return isDesktop();
    }
    return true;
}

/**
 * Re-derive transparent mode from the set of physically intersecting sentinels,
 * filtered by viewport-dependent activation (e.g. "desktop-only").
 */
function updateHeaderTransparency(): void {
    const header = document.getElementById("app-header");
    if (!header) return;

    // Blade `/marketplace` has no visible hero band — keep the header opaque so
    // nav links stay readable (avoid white-on-white from header--transparent).
    if (isMarketplaceCompactLayout()) {
        header.classList.remove("header--transparent");
        return;
    }

    const hasActive = Array.from(intersectingSentinels).some(isSentinelActive);
    header.classList.toggle("header--transparent", hasActive);
}

/**
 * Initialize the header mode manager.
 * Observes all elements with `data-header-sentinel` attribute.
 *
 * Sentinel modes:
 * - data-header-sentinel (or ="always"): triggers transparent mode on any viewport
 * - data-header-sentinel="desktop-only": triggers transparent mode only on desktop (>= 960px)
 */
export function initHeaderMode(): void {
    const header = document.getElementById("app-header");
    if (!header) return;

    // Suppress transitions on initial load so the header doesn't
    // visibly animate from opaque to transparent when the observer fires.
    header.classList.add("header--no-transition");

    // Create the observer — track raw intersection state, then derive transparency.
    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    intersectingSentinels.add(entry.target);
                } else {
                    intersectingSentinels.delete(entry.target);
                }
            });

            updateHeaderTransparency();

            // On the very first callback the correct state has just been applied
            // with transitions disabled. Wait one frame for the browser to paint,
            // then re-enable transitions for subsequent scroll changes.
            if (initialLoad) {
                initialLoad = false;
                requestAnimationFrame(() => {
                    header.classList.remove("header--no-transition");
                });
            }
        },
        {
            threshold: [0, 0.01],
            rootMargin: "0px",
        },
    );

    // Observe all sentinel elements
    observeAllSentinels();

    // Re-evaluate transparency on viewport resize (handles desktop-only sentinels).
    resizeHandler = () => {
        updateHeaderTransparency();
        if (needsMarketplaceFilterBarStickyTop()) {
            updateMarketplaceFilterBarStickyTop();
        }
    };
    window.addEventListener("resize", resizeHandler, { passive: true });

    initMarketplaceCollapse();
    initMarketplaceFilterBarSticky();
    syncSalesBannerHeight();

    if (needsMarketplaceFilterBarStickyTop()) {
        window.addEventListener("load", updateMarketplaceFilterBarStickyTop, {
            passive: true,
        });
    }

    // Re-observe on Inertia navigation
    document.addEventListener("inertia:navigate", handleNavigation);

    // Also handle page:load for Inertia pages
    document.addEventListener("inertia:finish", handleNavigation);
}

/**
 * Observe all elements with data-header-sentinel attribute.
 */
function observeAllSentinels(): void {
    if (!observer) return;

    // Clear previous observations
    intersectingSentinels.clear();

    // Observe all sentinel elements
    document.querySelectorAll("[data-header-sentinel]").forEach((el) => {
        observer!.observe(el);
    });
}

function needsMarketplaceFilterBarStickyTop(): boolean {
    return (
        isMarketplaceCompactLayout() ||
        document.querySelector(".marketplace-excerpt__action-bar") !== null
    );
}

/**
 * Bas de l'en-tête à sa place normale : quand il glisse hors de l'écran au défilement
 * (`mobile-scroll-chrome.ts`), son décalage ne doit pas fausser la position de la barre de filtres.
 */
function untransformedBottom(header: HTMLElement): number {
    const { transform } = getComputedStyle(header);
    const offsetY =
        transform && transform !== "none" && typeof DOMMatrixReadOnly !== "undefined"
            ? new DOMMatrixReadOnly(transform).m42
            : 0;

    return header.getBoundingClientRect().bottom - offsetY;
}

/**
 * Blade `/marketplace`: sticky offset for line 3 (filter bar) = sales banner + `#app-header`.
 */
export function updateMarketplaceFilterBarStickyTop(): void {
    const header = document.getElementById("app-header");
    const excerptBar = document.querySelector<HTMLElement>(
        ".marketplace-excerpt__action-bar",
    );

    if (excerptBar) {
        if (!header) {
            return;
        }
        const headerBottom = Math.round(untransformedBottom(header));
        document.documentElement.style.setProperty(
            MARKETPLACE_FILTER_BAR_STICKY_TOP_VAR,
            `${Math.max(0, headerBottom)}px`,
        );
        return;
    }

    if (!isMarketplaceCompactLayout()) {
        document.documentElement.style.removeProperty(
            MARKETPLACE_FILTER_BAR_STICKY_TOP_VAR,
        );
        return;
    }

    if (!header) {
        return;
    }

    const headerBottom = Math.round(untransformedBottom(header));

    document.documentElement.style.setProperty(
        MARKETPLACE_FILTER_BAR_STICKY_TOP_VAR,
        `${Math.max(0, headerBottom)}px`,
    );
}

function initMarketplaceFilterBarSticky(): void {
    cleanupMarketplaceFilterBarSticky();
    const hasExcerptBar = document.querySelector(
        ".marketplace-excerpt__action-bar",
    );
    if (!isMarketplaceCompactLayout() && !hasExcerptBar) {
        return;
    }

    updateMarketplaceFilterBarStickyTop();

    if (typeof ResizeObserver === "undefined") {
        return;
    }

    const header = document.getElementById("app-header");
    const banner = document.getElementById("sales-banner");
    filterBarStickyObserver = new ResizeObserver(() => {
        updateMarketplaceFilterBarStickyTop();
    });
    if (header) {
        filterBarStickyObserver.observe(header);
    }
    if (banner) {
        filterBarStickyObserver.observe(banner);
    }
}

function cleanupMarketplaceFilterBarSticky(): void {
    filterBarStickyObserver?.disconnect();
    filterBarStickyObserver = null;
    document.documentElement.style.removeProperty(
        MARKETPLACE_FILTER_BAR_STICKY_TOP_VAR,
    );
}

/**
 * Legacy Inertia `/marketplace-inertia` mobile: hide the header search row on scroll down.
 * Blade `/marketplace` keeps the header always visible (no collapse).
 */
function initMarketplaceCollapse(): void {
    cleanupMarketplaceCollapse();

    if (isMarketplaceCompactLayout()) {
        return;
    }

    const header = document.getElementById("app-header");
    if (!header || isDesktop()) return;

    const container = header.querySelector(
        '[data-page="vehicles.index"], [data-page="Vehicle/Index"]',
    );
    if (!container) return;

    let lastScrollY = window.scrollY;
    let isCollapsed = false;
    let ticking = false;

    const setCollapsed = (collapsed: boolean) => {
        if (isCollapsed === collapsed) return;
        isCollapsed = collapsed;
        header.classList.toggle("header--collapsed", collapsed);
    };

    collapseScrollHandler = () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
            const currentY = window.scrollY;

            if (currentY <= 5) {
                setCollapsed(false);
            } else if (currentY > lastScrollY + 8) {
                setCollapsed(true);
            } else if (currentY < lastScrollY - 8) {
                setCollapsed(false);
            }

            lastScrollY = currentY;
            ticking = false;
        });
    };
    window.addEventListener("scroll", collapseScrollHandler, { passive: true });
}

function cleanupMarketplaceCollapse(): void {
    if (collapseScrollHandler) {
        window.removeEventListener("scroll", collapseScrollHandler);
        collapseScrollHandler = null;
    }
    const header = document.getElementById("app-header");
    if (header) {
        header.classList.remove("header--collapsed");
    }
}

/**
 * Map the current URL pathname to the Laravel route name used in header CSS selectors.
 * Must cover every route targeted by _header.scss [data-page="…"] selectors.
 */
function resolveRouteName(pathname: string): string {
    const clean = pathname.split("?")[0].replace(/\/$/, "") || "/";
    if (clean === "/") return "home";
    if (clean === "/home-inertia") return "home.inertia";
    if (clean === "/marketplace") return "vehicles.index";
    if (clean === "/marketplace-inertia") return "vehicles.index.inertia";
    if (/^\/details-inertia\//.test(clean)) return "vehicles.show.inertia";
    if (/^\/details\//.test(clean)) return "vehicles.show";
    if (/^\/location-longue-duree-inertia-[^/]+$/.test(clean))
        return "vehicleMake.show.inertia";
    if (/^\/location-longue-duree-[^/]+\/[^/]+\/[^/]+/.test(clean))
        return "vehicleSpecification.show";
    if (/^\/location-longue-duree-[^/]+\/[^/]+/.test(clean))
        return "vehicleModel.show";
    if (/^\/location-longue-duree-[^/]+$/.test(clean))
        return "vehicleMake.show";
    return "";
}

/**
 * Sync the header container's data-page attribute with the current route
 * so CSS selectors that depend on it (compact mobile header, search box,
 * eagle logo, full-width container) apply after Inertia navigation.
 */
function updateHeaderDataPage(): void {
    const header = document.getElementById("app-header");
    if (!header) return;

    const container = header.querySelector(".header__container");
    if (!container) return;

    const routeName = resolveRouteName(window.location.pathname);
    container.setAttribute("data-page", routeName || "");
}

/**
 * Keep --sales-banner-height in sync with the banner's presence.
 * The height itself is CSS-fixed (SALES_BANNER_HEIGHT_PX), so no DOM measurement
 * is needed — this avoids a forced layout reflow on load.
 */
function syncSalesBannerHeight(): void {
    const banner = document.getElementById("sales-banner");
    if (!banner) {
        document.documentElement.style.setProperty("--sales-banner-height", "0px");
        return;
    }

    banner.classList.remove("sales-banner--home-hidden");

    document.documentElement.style.setProperty(
        "--sales-banner-height",
        `${SALES_BANNER_HEIGHT_PX}px`,
    );

    if (needsMarketplaceFilterBarStickyTop()) {
        updateMarketplaceFilterBarStickyTop();
    }
}

/**
 * Handle Inertia navigation by re-observing sentinels.
 */
function handleNavigation(): void {
    const header = document.getElementById("app-header");
    if (header) {
        header.classList.remove("header--transparent");
        header.classList.remove("header--collapsed");
    }

    intersectingSentinels.clear();
    cleanupMarketplaceCollapse();
    cleanupMarketplaceFilterBarSticky();

    requestAnimationFrame(() => {
        updateHeaderDataPage();
        syncSalesBannerHeight();
        observeAllSentinels();
        initMarketplaceCollapse();
        initMarketplaceFilterBarSticky();
    });
}

/**
 * Cleanup the header mode manager.
 */
export function cleanupHeaderMode(): void {
    if (observer) {
        observer.disconnect();
        observer = null;
    }

    intersectingSentinels.clear();
    cleanupMarketplaceCollapse();
    cleanupMarketplaceFilterBarSticky();

    if (resizeHandler) {
        window.removeEventListener("resize", resizeHandler);
        resizeHandler = null;
    }

    document.removeEventListener("inertia:navigate", handleNavigation);
    document.removeEventListener("inertia:finish", handleNavigation);
    window.removeEventListener("load", updateMarketplaceFilterBarStickyTop);
}

// Auto-initialize on DOMContentLoaded if this script is loaded directly
if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initHeaderMode);
    } else {
        // DOM is already ready
        initHeaderMode();
    }
}
